import { Component, Input, OnDestroy, Output, OnInit, EventEmitter, AfterViewChecked, signal, computed } from '@angular/core';
import { CommunityService } from 'src/app/services/community/community.service';
import { TicketsService } from '../services/tickets.service';
import { TicketDTO } from 'src/app/DTO/TicketDTO';
import { TicketPriceDTO } from 'src/app/DTO/TicketPriceDTO';
import { DateIsBetweenResult, Helpers } from 'src/app/helpers';
import { DateTime } from 'luxon';
import { TicketWaitingListInfoDTO } from 'src/app/DTO/tickets/TicketWaitingListInfoDTO';
import { DialogService } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { WaitingListTicketComponent } from 'src/app/pages/ticketing/components/waiting-list-ticket/waiting-list-ticket.component';
import { ticketI18nKey, ticketingExceededI18nKey } from 'src/app/helpers/ticket-translate.helper';

@Component({
  selector: 'app-tickets',
  templateUrl: './tickets.component.html',
  styleUrls: ['./tickets.component.scss'],
})
export class TicketsComponent implements OnInit, OnDestroy, AfterViewChecked {

  @Input() ticket: TicketDTO;
  @Input() waitingListUserDetails: TicketWaitingListInfoDTO | null = null;
  @Input() communityCode: string = '';
  /** When a coupon is applied, show this total instead of the cart total. */
  @Input() discountedTotal: number | null = null;
  @Input() originalTotal: number | null = null;
  @Output() emitNext: EventEmitter<any> = new EventEmitter<any>();
  @Output() swiperLoaded = new EventEmitter<boolean>();
  @Output() onAvailabilityChanged = new EventEmitter<number>();

  public ticketKey(key: string): string {
    return ticketI18nKey(this.communityCode, key);
  }

  public exceededKey(): string {
    return ticketingExceededI18nKey(this.communityCode);
  }

  public isLoading = true;

  public total = signal<number>(0);
  public totalQty = signal<number>(0);
  public totalQtyPurchases = signal<number>(0);

  public maxTiquetsPerUser = signal<number | null>(null);

  public fullFree = false;
  public inTime = false;
  public corpColor: string;
  public dateFrom: DateTime;
  public dateTo: DateTime;
  public availableTickets = signal<number | null>(null);
  public availableByPrice: { [id: number]: any } = {}
  public maxPerPrice: { [id: number]: number } = {}

  /** Precios visibles: con AccessCode solo el precio permitido por la lista de espera */
  public visiblePrices = computed(() => {
    const t = this.ticket;
    if (!t?.prices) return [];
    const w = this.waitingListUserDetails;
    if (w?.ticketPriceId != null) {
      return t.prices.filter(p => p.id === w.ticketPriceId);
    }
    return t.prices;
  });

  /** Máximo de entradas permitido por el enlace de lista de espera (null = sin límite por enlace) */
  public maxByAccessCode = computed(() => {
    const w = this.waitingListUserDetails;
    if (!w) return null;
    const already = w.alreadyBuyed ?? 0;
    return Math.max(0, (w.people ?? 0) - already);
  });

  private communityId: number;
  private refreshRemainingInterval: any;

  constructor(
    private communityService: CommunityService,
    private ticketsService: TicketsService,
    private dialogService: DialogService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.init();
    this.refreshAvailability();
    this.refreshRemainingInterval = setInterval(() => {
      this.refreshAvailability();
    }, 10000);
  }

  ngOnDestroy() {
    clearInterval(this.refreshRemainingInterval);
  }

  ngAfterViewChecked(): void {

  }

  public openWaitingListForPrice(priceId: number) {
    const ref = this.dialogService.open(WaitingListTicketComponent, {
      closable: true,
      position: 'top',
      data: { ticket: this.ticket, ticketPriceId: priceId, communityName: this.communityCode },
      header: this.translate.instant('INSCRIPTIONS.WaitingList.Title') + ': ' + this.ticket.title,
      styleClass: 'top-position-class form-modal waiting-list-modal',
      width: '1000px',
      appendTo: 'body'
    });
    ref.onClose.subscribe(() => this.refreshAvailability());
  }

  public onChangeQty(event, p: TicketPriceDTO) {
    p.qty = event;
    p.totalPerQty = p.totalWithFee * p.qty;

    this.total.set(this.ticket.prices.reduce((sum, current) => sum + (current.totalPerQty || 0), 0));
    this.totalQty.set(this.ticket.prices.reduce((sum, current) => sum + (current.qty || 0), 0));

    this.emitData();
  }

  public emitData() {
    const data = {
      total: this.total(),
      totalQty: this.totalQty(),
      prices: {}      
    };

    this.ticket.prices.forEach(x => {
      data.prices[x.id] = x.qty || 0;
    });    

    this.emitNext.emit(data);
  }

  private init() {
    this.communityService.Get()
      .subscribe((result) => {
        this.communityId = result.id;
        this.corpColor = result.color1;

        this.dateFrom = DateTime.fromJSDate(new Date(this.ticket.saleDateFrom));
        this.dateTo = DateTime.fromJSDate(new Date(this.ticket.saleDateTo));

        this.ticket.prices.forEach((x) => {
          x.totalPerQty = 0;
          x.qty = 0;
        })
        this.fullFree = this.ticket.prices.every(x => x.totalWithFee == 0);
        let chkDates = Helpers.CheckIfDateIsBetween(this.ticket.saleDateFrom, this.ticket.saleDateTo);
        this.inTime = (chkDates == DateIsBetweenResult.Between);
        this.total.set(0);
        this.totalQty.set(0);
        this.reloadMaxTickets();
      })
  }

  private refreshAvailability() {
    const accessCode = this.waitingListUserDetails?.accessCode ?? undefined;
    const includeWaitingList = !accessCode;
    const obs = this.communityCode
      ? this.ticketsService.getAvailabilityV2(this.communityCode, this.ticket.id, includeWaitingList, accessCode)
      : this.ticketsService.getAvailabilityV2(this.communityId, this.ticket.id);
    obs.subscribe(({ freePlaces, groups }) => {
        this.availableTickets.set(freePlaces);
        this.availableByPrice = Helpers.ConvertListToDict(groups, 'groupId');
        this.maxPerPrice = this.setMaxPerPrice();
        this.reloadMaxTickets();
        this.isLoading = false;
        this.onAvailabilityChanged.emit(this.availableTickets());
        this.swiperLoaded.emit(true);
      });
  }

  private reloadMaxTickets() {
    if (this.waitingListUserDetails) {
      const maxByAccess = this.maxByAccessCode();
      const available = this.availableTickets();
      const limit = available != null && maxByAccess != null
        ? Math.min(available, maxByAccess)
        : (maxByAccess ?? available ?? null);
      this.maxTiquetsPerUser.set(limit);
      return;
    }

    if (this.ticket?.maxByUser) {
      this.maxTiquetsPerUser.set(this.ticket.maxByUser - (this.totalQty() + this.totalQtyPurchases()));
    }

    if (this.availableTickets() != null){
      if (this.maxTiquetsPerUser() == null || this.availableTickets() < this.maxTiquetsPerUser()){
        this.maxTiquetsPerUser.set(this.availableTickets());
      }
    }
  }

  private setMaxPerPrice(): Record<string, number|null> {
    const {
      maxByUser,
      maxSalesPurchase
    } = this.ticket;

    const totalAvailable = this.availableTickets() ?? Infinity;
    const accessCodeLimit = this.maxByAccessCode() ?? Infinity;
    const useAccessCodeOnly = !!this.waitingListUserDetails;
    const userLimit  = useAccessCodeOnly ? Infinity : (maxByUser        ?? Infinity);
    const salesLimit = useAccessCodeOnly ? Infinity : (maxSalesPurchase ?? Infinity);

    const pricesToUse = this.waitingListUserDetails ? this.visiblePrices() : this.ticket.prices;

    return pricesToUse.reduce((acc, price) => {
      const freePlaces = this.availableByPrice[price.id]?.freePlaces ?? Infinity;
      const limit = Math.min(freePlaces, userLimit, salesLimit, accessCodeLimit, totalAvailable);
      acc[price.id] = Number.isFinite(limit) ? limit : null;
      return acc;
    }, {} as Record<string, number|null>);
  }
}
